import { adminService } from "@/api/admin/admin.service";
import { TAdminCompany, TCompanySection } from "@/api/admin/admin.types";
import { RouteLink } from "@/components/ui";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/Skeleton";
import { extractErrorMessage } from "@/utils";
import { ArrowLeft, Building2, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "-";
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function Detail({ label, value, mono = false }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className={`mt-0.5 break-all text-sm ${mono ? "font-mono text-xs" : ""}`}>{value ?? "-"}</p>
    </div>
  );
}

type TProps = {
  companyId: string;
};

export function CompanyDetailPage({ companyId }: TProps) {
  const [company, setCompany] = useState<TAdminCompany | null>(null);
  const [sections, setSections] = useState<TCompanySection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sectionsLoading, setSectionsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadCompany = useCallback(async () => {
    setIsLoading(true);
    const { data, error: err } = await adminService.getCompany(companyId);
    if (err) {
      setError(await extractErrorMessage(err));
      setCompany(null);
    } else if (data) {
      setError(null);
      setCompany(data.data);
    }
    setIsLoading(false);
  }, [companyId]);

  const loadSections = useCallback(async () => {
    setSectionsLoading(true);
    const { data, error: err } = await adminService.getCompanySections(companyId);
    if (!err && data && Array.isArray(data.data)) setSections(data.data);
    setSectionsLoading(false);
  }, [companyId]);

  useEffect(() => {
    void loadCompany();
    void loadSections();
  }, [loadCompany, loadSections]);

  return (
    <div className="h-full overflow-y-auto bg-muted p-4 sm:p-6">
      <div className="mx-auto max-w-5xl space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <RouteLink to="/app/admin/companies">
              <Button
                variant="outline"
                size="sm"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Companies
              </Button>
            </RouteLink>
            <div>
              <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Company Details</h1>
              <p className="mt-1 font-mono text-xs text-muted-foreground">{companyId}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void loadCompany();
              void loadSections();
            }}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        {isLoading ? (
          <Card>
            <CardContent className="space-y-4 pt-6">
              <Skeleton className="h-6 w-1/3" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-1/4" />
            </CardContent>
          </Card>
        ) : error ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-4 py-12">
              <p className="text-destructive">Error: {error}</p>
              <Button
                variant="outline"
                onClick={() => void loadCompany()}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : company ? (
          <>
            {/* Company card */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-wrap items-center gap-3">
                  <Building2 className="h-6 w-6 text-muted-foreground" />
                  <h2 className="text-xl font-semibold text-foreground">{company.name}</h2>
                  <Badge variant={company.role === "FUNDRAISER" ? "default" : "secondary"}>
                    {company.role === "FUNDRAISER" ? "Fundraiser" : "Investor"}
                  </Badge>
                  <Badge
                    variant="outline"
                    className={
                      company.onboarding_completed
                        ? "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400"
                        : "border-border bg-muted text-muted-foreground"
                    }
                  >
                    Onboarding {company.onboarding_completed ? "completed" : "pending"}
                  </Badge>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Detail
                    label="Owner (User ID)"
                    value={company.user_id}
                    mono
                  />
                  <Detail
                    label="Summary Generation"
                    value={company.summary_generation_status}
                  />
                  <Detail
                    label="Created"
                    value={fmtDate(company.created_at)}
                  />
                  <Detail
                    label="Updated"
                    value={fmtDate(company.updated_at)}
                  />
                </div>
                {company.onboarding_summary && (
                  <div className="mt-4 border-t border-border pt-4">
                    <p className="mb-1 text-xs font-medium text-muted-foreground">Onboarding Summary</p>
                    <p className="whitespace-pre-wrap text-sm text-foreground">{company.onboarding_summary}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sections */}
            <Card>
              <CardHeader>
                <CardTitle>Sections</CardTitle>
              </CardHeader>
              <CardContent>
                {sectionsLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Skeleton
                        key={i}
                        className="h-10 w-full"
                      />
                    ))}
                  </div>
                ) : sections.length === 0 ? (
                  <p className="py-4 text-center text-sm text-muted-foreground">No sections found for this company</p>
                ) : (
                  <div className="space-y-2">
                    {sections.map((section) => (
                      <div
                        key={section.id}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-card px-4 py-3"
                      >
                        <div>
                          <p className="text-sm font-medium text-foreground">{section.name}</p>
                          {section.description && (
                            <p className="text-xs text-muted-foreground">{section.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {section.role_type && (
                            <Badge variant="outline">{section.role_type}</Badge>
                          )}
                          <Badge
                            variant="outline"
                            className={
                              section.is_active
                                ? "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400"
                                : "border-border bg-muted text-muted-foreground"
                            }
                          >
                            {section.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : null}
      </div>
    </div>
  );
}
